/**
 * L'écart entre notre horloge et celle de la séance.
 *
 * # Pourquoi une référence, et pas une mesure entre pairs
 *
 * Une note ne porte pas un instant mais une position sur une grille partagée.
 * Pour que cette position veuille dire la même chose ailleurs, il faut savoir
 * de combien nos horloges diffèrent — et ça, une durée d'aller-retour ne le
 * dit pas : il faut la *valeur de temps* d'un tiers.
 *
 * Ce tiers est le service de temps. S'aligner contre lui plutôt que deux à deux
 * a deux vertus. D'abord le trajet est **une seule jambe** — la nôtre —, là où
 * mesurer un pair traverse la sienne aussi, puisque tout passe par le SFU.
 * Ensuite les erreurs ne s'enchaînent pas : chacun est à une mesure de la même
 * référence, au lieu de s'aligner sur quelqu'un qui s'est aligné sur quelqu'un.
 *
 * # Ce qui fait la qualité d'une mesure
 *
 * L'amplitude du ping s'annule exactement : si l'aller vaut le retour, un
 * trajet de 200 ms donne une mesure parfaite. Ce qui subsiste, c'est la moitié
 * de l'**asymétrie** — et elle, elle varie d'un paquet à l'autre.
 *
 * D'où la règle : plusieurs relevés, et on garde **celui dont l'aller-retour
 * est le plus court**. C'est l'heuristique de NTP, et elle bat la moyenne :
 * un aller-retour court est celui où personne n'a fait la queue, donc le moins
 * asymétrique. Moyenner reviendrait à intégrer les relevés pollués.
 */

/** Ce que le service de temps rend. */
interface ClockReading {
  /** Son horloge murale, en millisecondes. */
  now: number;
}

export interface ClockOffset {
  /**
   * À ajouter à notre horloge pour obtenir celle du service.
   *
   * Positif : le service est en avance sur nous.
   */
  offsetMs: number;
  /** L'aller-retour du relevé retenu. Sa petitesse fait sa qualité. */
  rttMs: number;
}

/** Relevés pris avant de trancher. Assez pour que le plus court soit propre. */
const SAMPLES = 5;

/**
 * Un relevé jeté avant les autres, pour établir la connexion.
 *
 * Le premier appel vers une origine neuve paie la résolution DNS, la poignée
 * TCP et la négociation TLS — facilement cent millisecondes qui n'ont rien à
 * voir avec le trajet qu'on cherche à mesurer. Les suivants réutilisent la
 * connexion.
 *
 * Sans lui, un relevé sur cinq était perdu d'avance. La sélection au plus court
 * l'écartait déjà, donc la justesse ne changeait pas — mais on payait un
 * aller-retour pour rien, et sur une seule mesure on aurait pris la mauvaise.
 *
 * C'est l'équivalent navigateur d'une connexion ouverte à l'avance : on ne
 * pilote pas la session HTTP depuis une page, on la provoque.
 */
const WARM_UP = 1;

/** Au-delà, on considère que la mesure a échoué plutôt que d'attendre. */
const TIMEOUT_MS = 3000;

/**
 * Mesure l'écart contre le service de temps.
 *
 * Rend `null` si aucun relevé n'aboutit — pas de service configuré, injoignable,
 * trop lent. La séance fonctionne quand même : les notes circulent, seul leur
 * alignement se dégrade. Mieux vaut jouer désaligné que ne pas jouer.
 */
export async function measureClockOffset(
  clockUrl: string,
  now: () => number = () => performance.now(),
  fetchImpl: typeof fetch = fetch,
): Promise<ClockOffset | null> {
  if (clockUrl === "") {
    return null;
  }

  let best: ClockOffset | null = null;

  for (let sample = 0; sample < SAMPLES + WARM_UP; sample += 1) {
    const reading = await takeSample(clockUrl, now, fetchImpl);

    if (reading === null || sample < WARM_UP) {
      continue;
    }

    if (best === null || reading.rttMs < best.rttMs) {
      best = reading;
    }
  }

  return best;
}

async function takeSample(
  clockUrl: string,
  now: () => number,
  fetchImpl: typeof fetch,
): Promise<ClockOffset | null> {
  const sentAt = now();

  try {
    const response = await fetchImpl(`${clockUrl}/clock`, {
      signal: AbortSignal.timeout(TIMEOUT_MS),
      cache: "no-store",
    });

    if (!response.ok) {
      return null;
    }

    const receivedAt = now();
    const body = (await response.json()) as Partial<ClockReading>;

    if (typeof body.now !== "number" || !Number.isFinite(body.now)) {
      return null;
    }

    // Le service a lu son horloge à peu près au milieu de l'aller-retour :
    // c'est donc à cet instant-là que les deux valeurs se comparent.
    const rttMs = receivedAt - sentAt;

    return { offsetMs: body.now - (sentAt + rttMs / 2), rttMs };
  } catch {
    // Injoignable, expiré, réponse illisible : ce relevé ne compte pas. Un
    // autre suivra, et s'il n'en reste aucun l'appelant saura s'en passer.
    return null;
  }
}

/**
 * Cadence des relevés qui suivent le premier, en millisecondes.
 *
 * # Pourquoi remesurer
 *
 * L'écart n'était mesuré qu'une fois, à la jointure. Or `performance.now()`
 * n'est asservi à rien sur macOS ni sur iOS : c'est le quartz de la machine,
 * et deux quartz diffèrent de dix à cinquante millionièmes — trente-six à cent
 * quatre-vingts millisecondes par heure. Le trajet se mesure entre deux
 * lectures de l'horloge du service, une par poste (voir `transit.ts`) : cette
 * dérive y entre telle quelle. Dans un sens elle gonfle le trajet mesuré,
 * donc le délai de toute la salle ; dans l'autre les notes semblent arriver
 * en avance, le trajet se sous-estime, et les dépassements montent. Invisible sur une prise de cinq
 * minutes, visible sur une répétition d'une heure.
 *
 * Un relevé toutes les quinze secondes, c'est six requêtes minuscules ; la
 * dérive la plus forte y vaut trois quarts de milliseconde.
 */
export const REALIGN_INTERVAL_MS = 15_000;

/**
 * Correction maximale de l'écart tenu par relevé, en millisecondes.
 *
 * # Asservir, pas recopier
 *
 * Même discipline que le pont audio (`MAX_STEP_SECONDS` dans
 * `audio-timebase.ts`) : ce qu'on veut suivre et ce qu'on veut ignorer se
 * séparent par leur échelle. La dérive à suivre vaut au plus quelques dixièmes
 * de milliseconde entre deux relevés ; le bruit à ignorer est la moitié de
 * l'asymétrie du relevé retenu, quelques millisecondes sur un mauvais trajet.
 * Un pas d'un millième et demi par quart de minute donne cent millionièmes de
 * capacité — deux fois le pire quartz — et borne ce qu'un relevé pollué peut
 * déplacer à un millième et demi, moins que la gigue d'une seule note.
 *
 * Recopier chaque relevé ferait sauter l'origine du transport de plusieurs
 * millisecondes toutes les quinze secondes : la gigue qu'on a retirée deux
 * fois de la chaîne, remise à l'endroit où elle se voit le plus.
 */
const MAX_SLEW_MS = 1.5;

/**
 * Au-delà, ce n'est plus une dérive : on s'aligne d'un coup.
 *
 * Une horloge système remise à l'heure, un service relancé sur une autre
 * machine : l'écart change de dizaines de millisecondes et n'y reviendra pas.
 * L'asservir prendrait des minutes, à jouer à côté tout du long.
 */
const JUMP_MS = 30;

/**
 * Au-delà de ce multiple du meilleur aller-retour connu, un relevé ne décrit
 * plus le trajet : il décrit une attente.
 *
 * # Le relevé qui a fait sauter la salle
 *
 * Le 2026-09-13, le service de temps a été relancé pendant qu'une séance
 * tournait. Le relevé en cours a attendu que le conteneur revienne : un
 * aller-retour d'une seconde, dont on suppose l'aller égal au retour, alors
 * que tout le temps s'était passé d'un seul côté. L'écart en est ressorti
 * faux d'une demi-seconde, bien au-delà du saut franc, et il a été adopté
 * d'un coup. L'origine du poste a sauté d'autant, ses notes sont parties
 * datées d'une demi-seconde dans le passé, et le partenaire a mesuré un
 * « trajet » de 475 ms — que le service a aussitôt fait payer à toute la
 * salle.
 *
 * Un aller-retour qui vaut plusieurs fois le meilleur connu n'a donc pas voix
 * au chapitre, et surtout pas celle de faire sauter l'écart. Quatre fois, et
 * cinquante millisecondes de tolérance absolue : un réseau qui se dégrade pour
 * de bon passe encore, une attente ne passe pas.
 */
const RTT_TOLERANCE_FACTOR = 4;
const RTT_TOLERANCE_MS = 50;

/**
 * Relevés refusés d'affilée avant d'admettre que le trajet a changé pour de
 * bon. Deux minutes à quinze secondes le relevé : après quoi le suivant
 * redevient la référence, faute de quoi un réseau devenu durablement plus
 * lent ne serait plus jamais écouté.
 */
const REJECTIONS_BEFORE_REBASE = 8;

/**
 * L'écart tenu, relevé après relevé.
 *
 * Le premier relevé est adopté tel quel : il n'y a rien à quoi le comparer.
 * Les suivants le corrigent d'au plus `MAX_SLEW_MS`, sauf saut franc — et
 * seulement s'ils ont été pris sur un aller-retour digne du meilleur connu.
 * La dérive corrigée depuis le premier relevé est tenue pour être lue à
 * l'écran : c'est elle qui dit, au bout d'une heure, de combien les deux
 * quartz diffèrent — et si l'asservissement suit.
 */
export class ClockAligner {
  private held: number | null = null;
  private first: number | null = null;
  private bestRttMs: number | null = null;
  private rejections = 0;

  constructor(
    private readonly maxSlewMs = MAX_SLEW_MS,
    private readonly jumpMs = JUMP_MS,
  ) {}

  /** L'écart en vigueur, `null` avant le premier relevé. */
  get offsetMs(): number | null {
    return this.held;
  }

  /** De combien l'écart tenu s'est déplacé depuis le premier relevé retenu. */
  get driftMs(): number {
    if (this.held === null || this.first === null) {
      return 0;
    }

    return this.held - this.first;
  }

  /**
   * Prend un relevé en compte et rend l'écart tenu.
   *
   * Un relevé pris sur un aller-retour trop long pour décrire le trajet est
   * laissé de côté : l'écart tenu ne bouge pas. Voir `RTT_TOLERANCE_FACTOR`.
   */
  accept(measured: ClockOffset): number {
    if (this.held === null || this.bestRttMs === null) {
      this.held = measured.offsetMs;
      this.first = measured.offsetMs;
      this.bestRttMs = measured.rttMs;
      this.rejections = 0;

      return this.held;
    }

    if (!this.trustworthy(measured.rttMs)) {
      this.rejections += 1;

      if (this.rejections > REJECTIONS_BEFORE_REBASE) {
        // Le trajet a changé pour de bon : ce relevé devient la référence,
        // et l'écart le suit comme n'importe quel autre.
        this.bestRttMs = measured.rttMs;
        this.rejections = 0;
      } else {
        return this.held;
      }
    } else {
      this.rejections = 0;
      this.bestRttMs = Math.min(this.bestRttMs, measured.rttMs);
    }

    const error = measured.offsetMs - this.held;

    if (Math.abs(error) > this.jumpMs) {
      this.held = measured.offsetMs;
      this.first = measured.offsetMs;

      return this.held;
    }

    this.held += Math.sign(error) * Math.min(Math.abs(error), this.maxSlewMs);

    return this.held;
  }

  reset(): void {
    this.held = null;
    this.first = null;
    this.bestRttMs = null;
    this.rejections = 0;
  }

  private trustworthy(rttMs: number): boolean {
    if (this.bestRttMs === null) {
      return true;
    }

    return rttMs <= Math.max(this.bestRttMs * RTT_TOLERANCE_FACTOR, this.bestRttMs + RTT_TOLERANCE_MS);
  }
}
